import { supabase } from './supabase';

export type InvoiceStatus = 'pending_review' | 'confirmed' | 'rejected';

export type LineMatchMethod = string;
export type LineStatus = 'matched' | 'unmatched';

export type DiscrepancyType = 'shortage' | 'surplus' | 'damage' | 'price_mismatch' | 'other';
export type ResolutionStatus = 'open' | 'credit_requested' | 'credit_received' | 'resolved' | 'written_off';

export interface SupplierInvoice {
  id: string;
  company_id: string;
  supplier_id: string | null;
  supplier_name_raw: string | null;
  invoice_number: string | null;
  invoice_date: string | null;
  total_amount: number | null;
  total_vat: number | null;
  currency: string | null;
  status: InvoiceStatus;
  ocr_raw_json: Record<string, unknown> | null;
  image_storage_path: string | null;
  scanned_by: string | null;
  confirmed_by: string | null;
  confirmed_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface SupplierInvoiceLine {
  id: string;
  invoice_id: string;
  line_number: number;
  product_id: string | null;
  sku_raw: string | null;
  description_raw: string | null;
  quantity_invoiced: number | null;
  quantity_received: number | null;
  unit_price: number | null;
  line_total: number | null;
  match_method: LineMatchMethod | null;
  match_confidence: number | null;
  status: LineStatus;
  created_at: string;
}

export interface InvoiceDiscrepancy {
  id: string;
  invoice_id: string;
  line_id: string | null;
  company_id: string;
  discrepancy_type: DiscrepancyType;
  quantity_expected: number | null;
  quantity_actual: number | null;
  amount_difference: number | null;
  resolution_status: ResolutionStatus;
  credit_note_number: string | null;
  credit_amount: number | null;
  resolved_at: string | null;
  resolved_by: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface DiscrepancyWithInvoice extends InvoiceDiscrepancy {
  supplier_invoices: {
    supplier_name_raw: string | null;
    invoice_number: string | null;
  };
}

export interface SupplierNotificationContact {
  id: string;
  supplier_id: string;
  contact_name: string;
  phone: string | null;
  email: string | null;
  notify_discrepancies: boolean;
  notify_credits: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateDiscrepancyData {
  invoice_id: string;
  line_id?: string | null;
  company_id: string;
  discrepancy_type: DiscrepancyType;
  quantity_expected: number | null;
  quantity_actual: number | null;
  amount_difference: number | null;
  notes?: string | null;
}

export interface UpsertContactData {
  id?: string;
  supplier_id: string;
  contact_name: string;
  phone?: string | null;
  email?: string | null;
  notify_discrepancies?: boolean;
  notify_credits?: boolean;
  notes?: string | null;
}

export interface DashboardStats {
  totalInvoices: number;
  pendingReview: number;
  openDiscrepancies: number;
}

export const invoiceIntakeService = {
  async uploadAndScan(companyId: string, file: File) {
    try {
      const timestamp = Date.now();
      const storagePath = `${companyId}/${timestamp}_${file.name}`;

      const { error: uploadError } = await supabase.storage
        .from('invoice-images')
        .upload(storagePath, file);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        return { data: null, error: uploadError };
      }

      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]!);
      }
      const image_base64 = btoa(binary);

      const { data: { session } } = await supabase.auth.getSession();

      const payload = {
        image_base64,
        image_content_type: file.type,
        company_id: companyId,
        storage_path: storagePath,
      };

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invoice-ocr`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Edge function error:', errorText);
        return { data: null, error: { message: errorText } };
      }

      const data = await response.json();
      return { data, error: null };
    } catch (err) {
      console.error('Upload and scan exception:', err);
      return { data: null, error: err };
    }
  },

  async getInvoices(companyId: string, statusFilter?: string) {
    try {
      let query = supabase
        .from('supplier_invoices')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Get invoices error:', error);
        return { data: null, error };
      }

      return { data: data as SupplierInvoice[], error: null };
    } catch (err) {
      console.error('Get invoices exception:', err);
      return { data: null, error: err };
    }
  },

  async getInvoiceWithLines(invoiceId: string) {
    try {
      const { data: invoice, error: invoiceError } = await supabase
        .from('supplier_invoices')
        .select('*')
        .eq('id', invoiceId)
        .single();

      if (invoiceError) {
        console.error('Get invoice error:', invoiceError);
        return { invoice: null, lines: null, error: invoiceError };
      }

      const { data: lines, error: linesError } = await supabase
        .from('supplier_invoice_lines')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('line_number', { ascending: true });

      if (linesError) {
        console.error('Get invoice lines error:', linesError);
        return { invoice: invoice as SupplierInvoice, lines: null, error: linesError };
      }

      return {
        invoice: invoice as SupplierInvoice,
        lines: lines as SupplierInvoiceLine[],
        error: null,
      };
    } catch (err) {
      console.error('Get invoice with lines exception:', err);
      return { invoice: null, lines: null, error: err };
    }
  },

  async confirmInvoice(invoiceId: string, userId: string) {
    try {
      const { error } = await supabase
        .from('supplier_invoices')
        .update({
          status: 'confirmed',
          confirmed_by: userId,
          confirmed_at: new Date().toISOString(),
        })
        .eq('id', invoiceId);

      if (error) {
        console.error('Confirm invoice error:', error);
        return { error };
      }

      return { error: null };
    } catch (err) {
      console.error('Confirm invoice exception:', err);
      return { error: err };
    }
  },

  async rejectInvoice(invoiceId: string) {
    try {
      const { error } = await supabase
        .from('supplier_invoices')
        .update({ status: 'rejected' })
        .eq('id', invoiceId);

      if (error) {
        console.error('Reject invoice error:', error);
        return { error };
      }

      return { error: null };
    } catch (err) {
      console.error('Reject invoice exception:', err);
      return { error: err };
    }
  },

  async updateLineQuantityReceived(lineId: string, quantity: number) {
    try {
      const { error } = await supabase
        .from('supplier_invoice_lines')
        .update({ quantity_received: quantity })
        .eq('id', lineId);

      if (error) {
        console.error('Update line quantity error:', error);
        return { error };
      }

      return { error: null };
    } catch (err) {
      console.error('Update line quantity exception:', err);
      return { error: err };
    }
  },

  async createDiscrepancy(data: CreateDiscrepancyData) {
    try {
      const { data: discrepancy, error } = await supabase
        .from('invoice_discrepancies')
        .insert({
          invoice_id: data.invoice_id,
          line_id: data.line_id || null,
          company_id: data.company_id,
          discrepancy_type: data.discrepancy_type,
          quantity_expected: data.quantity_expected,
          quantity_actual: data.quantity_actual,
          amount_difference: data.amount_difference,
          notes: data.notes || null,
        })
        .select()
        .single();

      if (error) {
        console.error('Create discrepancy error:', error);
        return { data: null, error };
      }

      return { data: discrepancy as InvoiceDiscrepancy, error: null };
    } catch (err) {
      console.error('Create discrepancy exception:', err);
      return { data: null, error: err };
    }
  },

  async getDiscrepancies(companyId: string, statusFilter?: string) {
    try {
      let query = supabase
        .from('invoice_discrepancies')
        .select('*, supplier_invoices!inner(supplier_name_raw, invoice_number)')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (statusFilter) {
        query = query.eq('resolution_status', statusFilter);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Get discrepancies error:', error);
        return { data: null, error };
      }

      return { data: data as DiscrepancyWithInvoice[], error: null };
    } catch (err) {
      console.error('Get discrepancies exception:', err);
      return { data: null, error: err };
    }
  },

  async updateDiscrepancyStatus(id: string, resolution_status: string, notes?: string) {
    try {
      const updates: Record<string, unknown> = { resolution_status };
      if (notes !== undefined) {
        updates.notes = notes;
      }

      const { error } = await supabase
        .from('invoice_discrepancies')
        .update(updates)
        .eq('id', id);

      if (error) {
        console.error('Update discrepancy status error:', error);
        return { error };
      }

      return { error: null };
    } catch (err) {
      console.error('Update discrepancy status exception:', err);
      return { error: err };
    }
  },

  async getSupplierContacts(supplierId: string) {
    try {
      const { data, error } = await supabase
        .from('supplier_notification_contacts')
        .select('*')
        .eq('supplier_id', supplierId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Get supplier contacts error:', error);
        return { data: null, error };
      }

      return { data: data as SupplierNotificationContact[], error: null };
    } catch (err) {
      console.error('Get supplier contacts exception:', err);
      return { data: null, error: err };
    }
  },

  async upsertSupplierContact(contact: UpsertContactData) {
    try {
      const { data, error } = await supabase
        .from('supplier_notification_contacts')
        .upsert({
          ...(contact.id ? { id: contact.id } : {}),
          supplier_id: contact.supplier_id,
          contact_name: contact.contact_name,
          phone: contact.phone || null,
          email: contact.email || null,
          notify_discrepancies: contact.notify_discrepancies ?? true,
          notify_credits: contact.notify_credits ?? true,
          notes: contact.notes || null,
        })
        .select()
        .single();

      if (error) {
        console.error('Upsert supplier contact error:', error);
        return { data: null, error };
      }

      return { data: data as SupplierNotificationContact, error: null };
    } catch (err) {
      console.error('Upsert supplier contact exception:', err);
      return { data: null, error: err };
    }
  },

  async deleteSupplierContact(id: string) {
    try {
      const { error } = await supabase
        .from('supplier_notification_contacts')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Delete supplier contact error:', error);
        return { error };
      }

      return { error: null };
    } catch (err) {
      console.error('Delete supplier contact exception:', err);
      return { error: err };
    }
  },

  async getDashboardStats(companyId: string): Promise<{ data: DashboardStats | null; error: unknown }> {
    try {
      const [totalResult, pendingResult, discrepanciesResult] = await Promise.all([
        supabase
          .from('supplier_invoices')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId),
        supabase
          .from('supplier_invoices')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('status', 'pending_review'),
        supabase
          .from('invoice_discrepancies')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('resolution_status', 'open'),
      ]);

      const error = totalResult.error || pendingResult.error || discrepanciesResult.error;
      if (error) {
        console.error('Get dashboard stats error:', error);
        return { data: null, error };
      }

      return {
        data: {
          totalInvoices: totalResult.count ?? 0,
          pendingReview: pendingResult.count ?? 0,
          openDiscrepancies: discrepanciesResult.count ?? 0,
        },
        error: null,
      };
    } catch (err) {
      console.error('Get dashboard stats exception:', err);
      return { data: null, error: err };
    }
  },

  async getImageUrl(storagePath: string): Promise<string | null> {
    try {
      const { data, error } = await supabase.storage
        .from('invoice-images')
        .createSignedUrl(storagePath, 3600);

      if (error) {
        console.error('Get image URL error:', error);
        return null;
      }

      return data.signedUrl;
    } catch (err) {
      console.error('Get image URL exception:', err);
      return null;
    }
  },
};
